import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import React, { Fragment, useEffect, useState } from "react";
import Select from "react-select";
import { getVehicleswithoutscreen } from "../../../api/vehicle.api";
import { getAllRestaurants } from "../../../api/restaurants.api"; // Assuming this API exists
import { errorMsg, success } from "../../../utils/notifications";
import { addScreen, updateScreen } from "../../../api/screens.api";

const statusOptions = [
  { value: "Under Maintenance", label: "Under Maintenance" },
  { value: "Retired", label: "Retired" },
  { value: "Active", label: "Active" },
];

const typeOptions = [
  { value: "vehicle", label: "Vehicle" },
  { value: "restaurant", label: "Restaurant" },
];

const AddScreen = ({
  open,
  setOpen,
  screenData,
  initialData,
}) => {
  const [addScreenData, setAddScreenData] = useState(screenData || initialData);
  const [vehiclesDropdownData, setVehiclesDropdownData] = useState([]);
  const [restaurantsDropdownData, setRestaurantsDropdownData] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [selectedType, setSelectedType] = useState(null);

  useEffect(() => {
    setAddScreenData(screenData || initialData);
    setSelectedType(screenData?.screenType || "vehicle");

    // Initialize selected vehicle or restaurant correctly
    if (screenData?.screenType === "vehicle" && screenData?.vehicle) {
      setSelectedVehicle({
        value: screenData.vehicle._id,
        label: `${screenData.vehicle.brand} ${screenData.vehicle.model}, ${screenData.vehicle.registrationNumber}`,
      });
    } else {
      setSelectedVehicle(null); // No vehicle selected
    }

    if (screenData?.screenType === "restaurant" && screenData?.restaurant) {
      setSelectedRestaurant({
        value: screenData.restaurant._id,
        label: screenData.restaurant.name,
      });
    } else {
      setSelectedRestaurant(null);
    }
  }, [screenData]);

  useEffect(() => {
    // Fetch all vehicles and restaurants
    getVehicleswithoutscreen({ withoutScreen: true })
      .then((res) => {
        setVehiclesDropdownData(res?.data?.vehicles || []);
      })
      .catch((error) => {
        console.error("Error fetching vehicles:", error);
      });

    getAllRestaurants()
      .then((res) => {
        setRestaurantsDropdownData(res?.data || []);
      })
      .catch((error) => {
        console.error("Error fetching restaurants:", error);
      });
  }, []);
  useEffect(() => {
    if (addScreenData.status === "Active") {
      setAddScreenData({
        ...addScreenData,
        vehicle: "", // No vehicle selected
      });
      setSelectedVehicle({ value: "", label: "Select Vehicle" }); // Placeholder value
    }
  }, [addScreenData.status]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAddScreenData({
      ...addScreenData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log(addScreenData);

    if (addScreenData._id) {
      // Update existing screen
      updateScreen(addScreenData._id, addScreenData)
        .then((res) => {
          setAddScreenData(initialData);
          success("Successful", "Screen Updated Successfully");
          setOpen(false);
        })
        .catch((err) => {
          errorMsg("Oops", err?.response?.data?.error?._message);
          console.log(err);
        });
    } else {
      // Create a new screen
      addScreen(addScreenData)
        .then((res) => {
          setAddScreenData(initialData);
          success("Successful", "Screen Added Successfully");
          setOpen(false);
        })
        .catch((err) => {
          errorMsg("Oops", err?.response?.data?.error?._message);
          console.log(err);
        });
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="z-50" onClose={setOpen}>
        <Transition.Child
          enter="ease-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-500"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Panel className="mt-12 lg:ml-60 md:ml-60 ml-0 z-50 lg:w-1/2 md:w-1/2 w-4/5 fixed mx-auto rounded-lg bg-white pt-5 pb-4 text-left shadow-xl transition-all h-auto">
                <div className="absolute top-0 right-0 pt-4 pr-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                    onClick={() => setOpen(false)}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div>
                  <div className="sm:text-left">
                    <Dialog.Title
                      as="h3"
                      className="px-3 pb-5 text-xl font-medium border-b-2 border-gray-200 leading-6 text-gray-700"
                    >
                      {addScreenData._id ? "Update Screen" : "Add Screen"}
                    </Dialog.Title>
                    <div>
                      <form onSubmit={handleSubmit}>
                        <div className="h-[30rem] overflow-y-scroll z-50">
                          <div className="px-7 py-4">
                            {/* Type Selection */}
                            <div className="py-3">
                              <div className="grid grid-cols-12 items-center">
                                <div className="col-span-5">
                                  <label
                                    htmlFor="type"
                                    className="block text-base font-semibold leading-6 text-gray-800"
                                  >
                                    Screen Type
                                  </label>
                                </div>
                                <div className="col-span-7">
                                  <div className="mt-2">
                                    <Select
                                      name="type"
                                      options={typeOptions}
                                      value={typeOptions.find(
                                        (option) =>
                                          option.value === selectedType
                                      )}
                                      isDisabled // Disable type selection when updating
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            {/* {screens type} */}

                            {selectedType === "vehicle" && (
                              <div className="py-3">
                                <div className="grid grid-cols-12 items-center">
                                  <div className="col-span-5">
                                    <label
                                      htmlFor="vehicle"
                                      className="block text-base font-semibold leading-6 text-gray-800"
                                    >
                                      Vehicle
                                    </label>
                                  </div>
                                  <div className="col-span-7">
                                    <div className="mt-2">
                                      {addScreenData.status === "Active" ? (
                                        <Select
                                          name="vehicle"
                                          id="vehicle"
                                          placeholder="Select Vehicle"
                                          options={[
                                            {
                                              value: "",
                                              label: "Select Vehicle",
                                            }, // Default placeholder
                                            ...vehiclesDropdownData.map(
                                              (vehicle) => ({
                                                value: vehicle._id,
                                                label: `${vehicle.brand} ${vehicle.model}, ${vehicle.registrationNumber}`,
                                              })
                                            ),
                                          ]}
                                          value={
                                            selectedVehicle ||
                                            (vehiclesDropdownData.length > 0
                                              ? {
                                                  value:
                                                    vehiclesDropdownData[0]._id,
                                                  label: `${vehiclesDropdownData[0].brand} ${vehiclesDropdownData[0].model}, ${vehiclesDropdownData[0].registrationNumber}`,
                                                }
                                              : {
                                                  value: "",
                                                  label: "Select Vehicle",
                                                }) // Default value
                                          }
                                          onChange={(selectedOption) => {
                                            setAddScreenData({
                                              ...addScreenData,
                                              vehicle: selectedOption.value,
                                            });
                                            setSelectedVehicle(selectedOption);
                                          }}
                                          required
                                        />
                                      ) : (
                                        <p className="text-sm text-red-500">
                                          Please activate the screen to select a
                                          vehicle.
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {selectedType === "restaurant" && (
                              <div className="py-3">
                                <div className="grid grid-cols-12 items-center">
                                  <div className="col-span-5">
                                    <label
                                      htmlFor="restaurant"
                                      className="block text-base font-semibold leading-6 text-gray-800"
                                    >
                                      Restaurant
                                    </label>
                                  </div>
                                  <div className="col-span-7">
                                    <div className="mt-2">
                                      {addScreenData.status === "Active" ? (
                                        <Select
                                          name="restaurant"
                                          id="restaurant"
                                          placeholder="Restaurant"
                                          options={[
                                            ...restaurantsDropdownData.map(
                                              (restaurant) => ({
                                                value: restaurant._id,
                                                label: restaurant.name,
                                              })
                                            ),
                                          ]}
                                          value={
                                            selectedRestaurant ||
                                            (restaurantsDropdownData.length > 0
                                              ? {
                                                  value:
                                                    restaurantsDropdownData[0]
                                                      ._id,
                                                  label:
                                                    restaurantsDropdownData[0]
                                                      .name,
                                                }
                                              : {
                                                  value: "",
                                                  label: "Select Restaurant",
                                                }) // Default value
                                          }
                                          onChange={(selectedOption) => {
                                            setAddScreenData({
                                              ...addScreenData,
                                              restaurant: selectedOption.value,
                                            });
                                            setSelectedRestaurant(
                                              selectedOption
                                            );
                                          }}
                                          required
                                        />
                                      ) : (
                                        <p className="text-sm text-red-500">
                                          Please activate the screen to select a
                                          restaurant.
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Screen Model */}
                            <div className="py-3">
                              <div className="grid grid-cols-12 items-center">
                                <div className="col-span-5">
                                  <label
                                    htmlFor="model"
                                    className="block text-base font-semibold leading-6 text-gray-800"
                                  >
                                    Screen Model
                                  </label>
                                </div>
                                <div className="col-span-7">
                                  <div className="mt-2">
                                    <input
                                      type="text"
                                      name="model"
                                      placeholder="Enter Screen Model"
                                      value={addScreenData.model}
                                      onChange={handleChange}
                                      id="model"
                                      className="text-sm font-medium bg-white bg-opacity-80 px-3 block w-full rounded outline-0 py-1.5 text-gray-600 placeholder:text-gray-300 border border-gray-300 focus:border-2 focus:border-green-500 shadow-sm focus:ring-0 sm:text-sm sm:leading-6"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            {/* Status Dropdown */}
                            <div className="py-3">
                              <div className="grid grid-cols-12 items-center">
                                <div className="col-span-5">
                                  <label
                                    htmlFor="status"
                                    className="block text-base font-semibold leading-6 text-gray-800"
                                  >
                                    Status
                                  </label>
                                </div>
                                <div className="col-span-7">
                                  <div className="mt-2">
                                    <Select
                                      name="status"
                                      id="status"
                                      options={statusOptions}
                                      value={statusOptions.find(
                                        (option) =>
                                          option.value === addScreenData.status
                                      )}
                                      onChange={(selectedOption) => {
                                        setAddScreenData({
                                          ...addScreenData,
                                          status: selectedOption.value,
                                        });
                                      }}
                                      required
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                          <button
                            type="submit"
                            className="inline-flex w-full justify-center rounded-md bg-yellow-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 sm:ml-3 sm:w-auto"
                          >
                            {addScreenData._id ? "Update" : "Add"}
                          </button>
                          <button
                            type="button"
                            className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                            onClick={() => setOpen(false)}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default AddScreen;
